import toast from "react-hot-toast";

/**
 * Save a blob response from the API as a file.
 *
 * Axios is configured with responseType: "blob", so a FAILED request (404 "File not
 * found", 403, expired token, 500) still arrives as a Blob — writing it straight to
 * disk produced files like "document.pdf" containing JSON, which is why users saw
 * "Failed to load PDF document". This checks the status first and surfaces the real
 * server message instead of downloading a corrupt file.
 *
 * @param {object} response axios response (may be an error response)
 * @param {string} fileName name to save as — keep the stored file's real extension
 * @returns {Promise<boolean>} true when a file was downloaded
 */
export const downloadFileResponse = async (response, fileName) => {
  if (!response) {
    toast.error("Download failed — no response from the server.", { className: "errorToast" });
    return false;
  }

  const ok = response.status >= 200 && response.status < 300;

  if (!ok) {
    let message = "Download failed.";
    try {
      // The error body came through as a Blob — read it back out
      const text = response.data instanceof Blob ? await response.data.text() : null;
      if (text) {
        try {
          message = JSON.parse(text)?.message || message;
        } catch {
          // not JSON (e.g. an HTML error page) — keep the generic message
        }
      } else if (response.data?.message) {
        message = response.data.message;
      }
    } catch {
      // fall through with the generic message
    }
    toast.error(message, { className: "errorToast" });
    return false;
  }

  const contentType = response.headers?.["content-type"] || undefined;
  const blob = contentType ? new Blob([response.data], { type: contentType }) : new Blob([response.data]);

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return true;
};

export default downloadFileResponse;
